import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Popover,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  DragIndicator,
  Preview,
} from "@mui/icons-material";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  MatchingConfig,
  MatchingQuestion,
  MatchingQuestionType,
} from "../../types";

// ── Question type metadata ─────────────────────────────────────────────────────

const QUESTION_TYPE_LABEL_KEYS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]:
    "modals.matchingConfigEditor.questionTypeLabels.timeAvailability",
  [MatchingQuestionType.RolePreference]:
    "modals.matchingConfigEditor.questionTypeLabels.rolePreference",
  [MatchingQuestionType.Personality]:
    "modals.matchingConfigEditor.questionTypeLabels.personality",
  [MatchingQuestionType.PreferredTeammates]:
    "modals.matchingConfigEditor.questionTypeLabels.preferredTeammates",
  [MatchingQuestionType.AvoidTeammates]:
    "modals.matchingConfigEditor.questionTypeLabels.avoidTeammates",
  [MatchingQuestionType.CustomText]:
    "modals.matchingConfigEditor.questionTypeLabels.customText",
};

const QUESTION_TYPE_SUBLABEL_KEYS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]:
    "modals.matchingConfigEditor.questionTypeSublabels.timeAvailability",
  [MatchingQuestionType.RolePreference]:
    "modals.matchingConfigEditor.questionTypeSublabels.rolePreference",
  [MatchingQuestionType.Personality]:
    "modals.matchingConfigEditor.questionTypeSublabels.personality",
  [MatchingQuestionType.PreferredTeammates]:
    "modals.matchingConfigEditor.questionTypeSublabels.preferredTeammates",
  [MatchingQuestionType.AvoidTeammates]:
    "modals.matchingConfigEditor.questionTypeSublabels.avoidTeammates",
  [MatchingQuestionType.CustomText]:
    "modals.matchingConfigEditor.questionTypeSublabels.customText",
};

/** Chip colour per question type — makes chip visually distinct with good contrast */
const QUESTION_TYPE_CHIP_COLOR: Record<
  MatchingQuestionType,
  "default" | "error" | "info" | "secondary" | "success" | "warning"
> = {
  [MatchingQuestionType.TimeAvailability]: "info",
  [MatchingQuestionType.RolePreference]: "success",
  [MatchingQuestionType.Personality]: "secondary",
  [MatchingQuestionType.PreferredTeammates]: "warning",
  [MatchingQuestionType.AvoidTeammates]: "error",
  [MatchingQuestionType.CustomText]: "default",
};

/** Default affinity direction per question type */
const QUESTION_TYPE_DEFAULT_AFFINITY: Record<MatchingQuestionType, number> = {
  [MatchingQuestionType.TimeAvailability]: 1.0, // similarity — same schedule
  [MatchingQuestionType.RolePreference]: -1.0, // diversity — complementary roles
  [MatchingQuestionType.Personality]: -0.5, // lean diverse
  [MatchingQuestionType.PreferredTeammates]: 1.0, // similarity — honor preferences
  [MatchingQuestionType.AvoidTeammates]: -1.0, // anti-affinity — keep apart
  [MatchingQuestionType.CustomText]: 0.0, // neutral
};

/** Question types where the affinity slider should be hidden */
const HIDE_AFFINITY_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.TimeAvailability,
  MatchingQuestionType.PreferredTeammates,
  MatchingQuestionType.AvoidTeammates,
]);

/** Question types that are always optional — students may skip without answering */
const ALWAYS_OPTIONAL_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.PreferredTeammates,
  MatchingQuestionType.AvoidTeammates,
]);

/** Maximum number of matching criteria (questions) per config */
const MAX_CRITERIA = 4;

// Standard types that should only appear once
const SINGLETON_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.TimeAvailability,
  MatchingQuestionType.RolePreference,
  MatchingQuestionType.Personality,
  MatchingQuestionType.PreferredTeammates,
  MatchingQuestionType.AvoidTeammates,
]);

const DEFAULT_LABEL_KEYS: Record<MatchingQuestionType, string> = {
  [MatchingQuestionType.TimeAvailability]:
    "modals.matchingConfigEditor.defaultLabels.timeAvailability",
  [MatchingQuestionType.RolePreference]:
    "modals.matchingConfigEditor.defaultLabels.rolePreference",
  [MatchingQuestionType.Personality]:
    "modals.matchingConfigEditor.defaultLabels.personality",
  [MatchingQuestionType.PreferredTeammates]:
    "modals.matchingConfigEditor.defaultLabels.preferredTeammates",
  [MatchingQuestionType.AvoidTeammates]:
    "modals.matchingConfigEditor.defaultLabels.avoidTeammates",
  [MatchingQuestionType.CustomText]:
    "modals.matchingConfigEditor.defaultLabels.customText",
};

const DEFAULT_DESCRIPTION_KEYS: Record<MatchingQuestionType, string | null> = {
  [MatchingQuestionType.TimeAvailability]:
    "modals.matchingConfigEditor.defaultDescriptions.timeAvailability",
  [MatchingQuestionType.RolePreference]:
    "modals.matchingConfigEditor.defaultDescriptions.rolePreference",
  [MatchingQuestionType.Personality]:
    "modals.matchingConfigEditor.defaultDescriptions.personality",
  [MatchingQuestionType.PreferredTeammates]: null,
  [MatchingQuestionType.AvoidTeammates]: null,
  [MatchingQuestionType.CustomText]: null,
};

/** Types where the question label is preset and should not be editable by the instructor */
const PRESET_LABEL_TYPES = new Set<MatchingQuestionType>([
  MatchingQuestionType.TimeAvailability,
  MatchingQuestionType.RolePreference,
  MatchingQuestionType.Personality,
  MatchingQuestionType.PreferredTeammates,
  MatchingQuestionType.AvoidTeammates,
]);

/** Default role options for the Role Preference question (up to 5) */
const DEFAULT_ROLE_OPTIONS = ["Project Manager", "Individual Contributor"];

const MAX_ROLE_OPTIONS = 5;

// ── Survey preview ─────────────────────────────────────────────────────────────

const PREVIEW_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PREVIEW_SLOTS = ["Morning", "Afternoon", "Evening"];

function SurveyPreview({
  questions,
  t,
}: {
  questions: Partial<MatchingQuestion>[];
  t: TFunction;
}) {
  return (
    <Box>
      {questions.map((q, i) => {
        const roleOptions = (q.options as string[] | undefined)?.length
          ? (q.options as string[])
          : DEFAULT_ROLE_OPTIONS;
        const customOptions = (q.options as string[] | undefined) ?? [];
        return (
          <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography fontWeight={600} mb={0.5}>
              {q.label || t("modals.matchingConfigEditor.noLabelFallback")}
              {q.required && (
                <Typography component="span" color="error" ml={0.5}>
                  *
                </Typography>
              )}
            </Typography>
            {q.description && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                {q.description}
              </Typography>
            )}
            {/* Show a representative sample of each input type */}
            {q.questionType === MatchingQuestionType.TimeAvailability && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${PREVIEW_DAYS.length}, 1fr)`,
                  gap: 0.5,
                }}
              >
                {PREVIEW_DAYS.map((d) => (
                  <Typography
                    key={d}
                    variant="caption"
                    align="center"
                    fontWeight={600}
                    sx={{ py: 0.5 }}
                  >
                    {d}
                  </Typography>
                ))}
                {PREVIEW_SLOTS.map((slot) =>
                  PREVIEW_DAYS.map((d) => (
                    <Chip
                      key={`${d}-${slot}`}
                      label={slot.substring(0, 2)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.65rem", height: 22 }}
                    />
                  )),
                )}
              </Box>
            )}
            {q.questionType === MatchingQuestionType.RolePreference && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {roleOptions.map((r) => (
                  <Chip key={r} label={r} variant="outlined" size="small" />
                ))}
              </Box>
            )}
            {q.questionType === MatchingQuestionType.Personality && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {["INTJ", "INTP", "ENTJ", "ENTP", "INFJ", "INFP"].map((t) => (
                  <Chip key={t} label={t} variant="outlined" size="small" />
                ))}
                <Typography variant="caption" color="text.secondary">
                  {t("modals.matchingConfigEditor.previewAndMore")}
                </Typography>
              </Box>
            )}
            {(q.questionType === MatchingQuestionType.PreferredTeammates ||
              q.questionType === MatchingQuestionType.AvoidTeammates) && (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
              >
                {t("modals.matchingConfigEditor.previewSearchHint")}
              </Typography>
            )}
            {q.questionType === MatchingQuestionType.CustomText &&
              (customOptions.length > 0 ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {customOptions.map((opt) => (
                    <Chip
                      key={opt}
                      label={opt}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t("modals.matchingConfigEditor.previewNoOptions")}
                </Typography>
              ))}
          </Paper>
        );
      })}
    </Box>
  );
}

// ── Draft types ────────────────────────────────────────────────────────────────

type DraftConfig = {
  name: string;
  description: string;
  isTemplate: boolean;
  questions: Partial<MatchingQuestion>[];
};

function defaultConfig(t: TFunction): DraftConfig {
  return {
    name: t("modals.matchingConfigEditor.defaultConfigName"),
    description: "",
    isTemplate: false,
    questions: [],
  };
}

function configFromExisting(config: MatchingConfig): DraftConfig {
  return {
    name: config.name,
    description: config.description,
    isTemplate: config.isTemplate,
    questions: config.questions.map((q) => ({
      ...q,
      // Normalize: teammate questions are always optional regardless of DB value
      required: ALWAYS_OPTIONAL_TYPES.has(
        q.questionType as MatchingQuestionType,
      )
        ? false
        : q.required,
    })),
  };
}

// ── Affinity slider label ──────────────────────────────────────────────────────

function affinityLabel(val: number, t: TFunction): string {
  if (val <= -0.75)
    return t("modals.matchingConfigEditor.affinityMaxDiversity");
  if (val <= -0.25)
    return t("modals.matchingConfigEditor.affinityPreferDiverse");
  if (val < 0.25) return t("modals.matchingConfigEditor.affinityBalanced");
  if (val < 0.75) return t("modals.matchingConfigEditor.affinityPreferSimilar");
  return t("modals.matchingConfigEditor.affinityMaxSimilarity");
}

// ── Props & component ──────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  existing?: MatchingConfig;
  onClose: () => void;
  onSave: (draft: DraftConfig) => void;
  saving?: boolean;
}

export function MatchingConfigEditor({
  open,
  existing,
  onClose,
  onSave,
  saving,
}: Props) {
  const [t] = useTranslation();
  const [draft, setDraft] = useState<DraftConfig>(
    existing ? configFromExisting(existing) : defaultConfig(t),
  );
  const [addPopoverAnchor, setAddPopoverAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  // DnD state
  const dragIndex = useRef<number | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(existing ? configFromExisting(existing) : defaultConfig(t));
    }
  }, [open, existing, t]);

  const set = <K extends keyof DraftConfig>(key: K, value: DraftConfig[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const setQuestion = (i: number, patch: Partial<MatchingQuestion>) =>
    setDraft((d) => {
      const questions = [...d.questions];
      questions[i] = { ...questions[i], ...patch };
      return { ...d, questions };
    });

  const addQuestion = (type: MatchingQuestionType) => {
    setAddPopoverAnchor(null);
    setDraft((d) => ({
      ...d,
      questions: [
        ...d.questions,
        {
          questionType: type,
          label: t(DEFAULT_LABEL_KEYS[type]),
          description: DEFAULT_DESCRIPTION_KEYS[type]
            ? t(DEFAULT_DESCRIPTION_KEYS[type] as string)
            : "",
          weight: 5,
          order: d.questions.length,
          options:
            type === MatchingQuestionType.RolePreference
              ? DEFAULT_ROLE_OPTIONS
              : [],
          required: !ALWAYS_OPTIONAL_TYPES.has(type),
          similarityPreference: QUESTION_TYPE_DEFAULT_AFFINITY[type],
        },
      ],
    }));
  };

  const removeQuestion = (i: number) =>
    setDraft((d) => ({
      ...d,
      questions: d.questions.filter((_, idx) => idx !== i),
    }));

  const moveQuestion = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const questions = [...d.questions];
      const target = i + dir;
      if (target < 0 || target >= questions.length) return d;
      [questions[i], questions[target]] = [questions[target], questions[i]];
      return { ...d, questions };
    });

  // Which singleton types are already in the config?
  const usedSingletons = new Set(
    draft.questions
      .map((q) => q.questionType as MatchingQuestionType)
      .filter((t) => SINGLETON_TYPES.has(t)),
  );

  // Available types for the Add popover (hide already-used singletons)
  const availableTypes = Object.values(MatchingQuestionType).filter(
    (t) => !SINGLETON_TYPES.has(t) || !usedSingletons.has(t),
  );

  // HTML5 DnD handlers
  const handleDragStart = (i: number) => {
    dragIndex.current = i;
  };

  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setDraft((d) => {
      const questions = [...d.questions];
      const [removed] = questions.splice(from, 1);
      questions.splice(i, 0, removed);
      dragIndex.current = i;
      return { ...d, questions };
    });
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {existing
            ? t("modals.matchingConfigEditor.editTitle")
            : t("modals.matchingConfigEditor.newTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Config name + template toggle — inline */}
            <Box display="flex" alignItems="flex-start" gap={2}>
              <TextField
                label={t("modals.matchingConfigEditor.nameLabel")}
                fullWidth
                value={draft.name}
                onChange={(e) => set("name", e.target.value)}
                required
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.isTemplate}
                    onChange={(e) => set("isTemplate", e.target.checked)}
                  />
                }
                label={t("modals.matchingConfigEditor.saveAsTemplate")}
                sx={{ whiteSpace: "nowrap", mt: 1 }}
              />
            </Box>

            {/* Description — only shown when saving as template */}
            {draft.isTemplate && (
              <TextField
                label={t("modals.matchingConfigEditor.descriptionLabel")}
                fullWidth
                multiline
                rows={2}
                value={draft.description}
                onChange={(e) => set("description", e.target.value)}
                helperText={t("modals.matchingConfigEditor.descriptionHelper")}
              />
            )}

            {/* Survey questions */}
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Box>
                  <Typography variant="subtitle2">
                    {t("modals.matchingConfigEditor.surveyQuestions")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("modals.matchingConfigEditor.questionsHelper")}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Preview />}
                    onClick={() => setPreviewOpen(true)}
                    disabled={draft.questions.length === 0}
                  >
                    {t("modals.matchingConfigEditor.previewSurvey")}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={(e) => setAddPopoverAnchor(e.currentTarget)}
                    disabled={
                      availableTypes.length === 0 ||
                      draft.questions.length >= MAX_CRITERIA
                    }
                  >
                    {t("modals.matchingConfigEditor.addQuestion")}
                  </Button>
                  {draft.questions.length >= MAX_CRITERIA && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      {t("modals.matchingConfigEditor.maxCriteria")}
                    </Typography>
                  )}
                </Box>
              </Box>

              {draft.questions.length === 0 && (
                <Typography
                  color="text.secondary"
                  variant="body2"
                  align="center"
                  py={3}
                >
                  {t("modals.matchingConfigEditor.noQuestions")}
                </Typography>
              )}

              {draft.questions.map((q, i) => {
                const qType = (q.questionType ??
                  MatchingQuestionType.CustomText) as MatchingQuestionType;
                const chipColor = QUESTION_TYPE_CHIP_COLOR[qType];
                const affinity = q.similarityPreference ?? 0;
                return (
                  <Box
                    key={i}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 1,
                      cursor: "grab",
                      "&:active": { cursor: "grabbing" },
                      bgcolor: "background.paper",
                      overflow: "hidden",
                    }}
                  >
                    {/* ── Header row: drag handle, type badge, reorder arrows, delete ── */}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        px: 1.5,
                        py: 1,
                        bgcolor: "action.hover",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <DragIndicator
                        fontSize="small"
                        sx={{ color: "text.disabled", flexShrink: 0 }}
                      />
                      <Chip
                        label={t(QUESTION_TYPE_LABEL_KEYS[qType])}
                        size="small"
                        color={chipColor}
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexGrow: 1, ml: 0.5 }}
                      >
                        {t(QUESTION_TYPE_SUBLABEL_KEYS[qType])}
                      </Typography>
                      <Tooltip title={t("modals.matchingConfigEditor.moveUp")}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label={t(
                              "modals.matchingConfigEditor.moveUp",
                            )}
                            disabled={i === 0}
                            onClick={() => moveQuestion(i, -1)}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={t("modals.matchingConfigEditor.moveDown")}
                      >
                        <span>
                          <IconButton
                            size="small"
                            aria-label={t(
                              "modals.matchingConfigEditor.moveDown",
                            )}
                            disabled={i === draft.questions.length - 1}
                            onClick={() => moveQuestion(i, 1)}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={t("modals.matchingConfigEditor.removeQuestion")}
                      >
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={t(
                            "modals.matchingConfigEditor.deleteAria",
                          )}
                          onClick={() => removeQuestion(i)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* ── Body: label, description, options, affinity ── */}
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        {/* Question label — static for preset types, editable for custom */}
                        {PRESET_LABEL_TYPES.has(qType) ? (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {t("modals.matchingConfigEditor.questionLabel")}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.25 }}>
                              {q.label}
                            </Typography>
                          </Box>
                        ) : (
                          <TextField
                            size="small"
                            label={t(
                              "modals.matchingConfigEditor.questionLabel",
                            )}
                            fullWidth
                            value={q.label ?? ""}
                            onChange={(e) =>
                              setQuestion(i, { label: e.target.value })
                            }
                          />
                        )}

                        {/* Description */}
                        <TextField
                          size="small"
                          label={t(
                            "modals.matchingConfigEditor.descriptionOptional",
                          )}
                          fullWidth
                          value={q.description ?? ""}
                          onChange={(e) =>
                            setQuestion(i, { description: e.target.value })
                          }
                        />

                        {/* Role options for RolePreference — editable chips (up to 5) */}
                        {qType === MatchingQuestionType.RolePreference && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              {t(
                                "modals.matchingConfigEditor.roleOptionsHeading",
                                { max: MAX_ROLE_OPTIONS },
                              )}
                            </Typography>
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              gap={0.5}
                              mb={0.5}
                            >
                              {(
                                (q.options as string[]) ?? DEFAULT_ROLE_OPTIONS
                              ).map((role: string) => (
                                <Chip
                                  key={role}
                                  label={role}
                                  size="small"
                                  onDelete={() => {
                                    const current =
                                      (q.options as string[]) ??
                                      DEFAULT_ROLE_OPTIONS;
                                    setQuestion(i, {
                                      options: current.filter(
                                        (r) => r !== role,
                                      ),
                                    });
                                  }}
                                />
                              ))}
                            </Box>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Add />}
                              aria-label={t(
                                "modals.matchingConfigEditor.addRoleAria",
                              )}
                              disabled={
                                (
                                  (q.options as string[]) ??
                                  DEFAULT_ROLE_OPTIONS
                                ).length >= MAX_ROLE_OPTIONS
                              }
                              onClick={() => {
                                const newRole = prompt(
                                  t(
                                    "modals.matchingConfigEditor.addRolePrompt",
                                  ),
                                );
                                if (newRole?.trim()) {
                                  const current =
                                    (q.options as string[]) ??
                                    DEFAULT_ROLE_OPTIONS;
                                  if (current.length < MAX_ROLE_OPTIONS) {
                                    setQuestion(i, {
                                      options: [...current, newRole.trim()],
                                    });
                                  }
                                }
                              }}
                            >
                              {t("modals.matchingConfigEditor.addRole")}
                            </Button>
                          </Box>
                        )}

                        {/* Options for CustomText — single-select choices */}
                        {qType === MatchingQuestionType.CustomText && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.5}
                            >
                              {t(
                                "modals.matchingConfigEditor.customOptionsHeading",
                              )}
                            </Typography>
                            {((q.options as string[]) ?? []).map(
                              (opt: string, oi: number) => (
                                <Box
                                  key={oi}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  mb={0.5}
                                >
                                  <TextField
                                    size="small"
                                    value={opt}
                                    onChange={(e) => {
                                      const current = [
                                        ...((q.options as string[]) ?? []),
                                      ];
                                      current[oi] = e.target.value;
                                      setQuestion(i, { options: current });
                                    }}
                                    sx={{ flexGrow: 1 }}
                                    placeholder={t(
                                      "modals.matchingConfigEditor.optionPlaceholder",
                                      { index: oi + 1 },
                                    )}
                                    inputProps={{
                                      "aria-label": t(
                                        "modals.matchingConfigEditor.optionAria",
                                        { index: oi + 1 },
                                      ),
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    aria-label={t(
                                      "modals.matchingConfigEditor.removeOptionAria",
                                      { index: oi + 1 },
                                    )}
                                    onClick={() => {
                                      const current = [
                                        ...((q.options as string[]) ?? []),
                                      ];
                                      current.splice(oi, 1);
                                      setQuestion(i, { options: current });
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              ),
                            )}
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Add />}
                              aria-label={t(
                                "modals.matchingConfigEditor.addOptionAria",
                              )}
                              onClick={() => {
                                const current = (q.options as string[]) ?? [];
                                setQuestion(i, { options: [...current, ""] });
                              }}
                            >
                              {t("modals.matchingConfigEditor.addOption")}
                            </Button>
                          </Box>
                        )}

                        {/* Per-question affinity slider (hidden for time, preferred/avoid teammates) */}
                        {!HIDE_AFFINITY_TYPES.has(qType) && (
                          <Box>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              mb={0}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t("modals.matchingConfigEditor.teamAffinity")}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {affinityLabel(affinity, t)}
                                {affinity === 0 && (
                                  <Tooltip
                                    title={t(
                                      "modals.matchingConfigEditor.balancedTooltip",
                                    )}
                                  >
                                    <span
                                      style={{ marginLeft: 4, cursor: "help" }}
                                    >
                                      ⓘ
                                    </span>
                                  </Tooltip>
                                )}
                              </Typography>
                            </Box>
                            <Slider
                              value={affinity}
                              min={-1}
                              max={1}
                              step={0.25}
                              marks={[
                                {
                                  value: -1,
                                  label: t(
                                    "modals.matchingConfigEditor.markDiverse",
                                  ),
                                },
                                {
                                  value: 0,
                                  label: t(
                                    "modals.matchingConfigEditor.markBalanced",
                                  ),
                                },
                                {
                                  value: 1,
                                  label: t(
                                    "modals.matchingConfigEditor.markSimilar",
                                  ),
                                },
                              ]}
                              onChange={(_e, v) =>
                                setQuestion(i, {
                                  similarityPreference: v as number,
                                })
                              }
                              size="small"
                              aria-label={t(
                                "modals.matchingConfigEditor.affinitySliderAria",
                                { index: i + 1 },
                              )}
                              sx={{
                                // Prevent edge mark labels from being clipped
                                // by the parent card's overflow:hidden
                                "& .MuiSlider-markLabel[data-index='0']": {
                                  transform: "translateX(0%)",
                                },
                                "& .MuiSlider-markLabel[data-index='2']": {
                                  transform: "translateX(-100%)",
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="light" onClick={onClose}>
            {t("modals.matchingConfigEditor.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => onSave(draft)}
            disabled={!draft.name || draft.questions.length === 0 || saving}
          >
            {saving
              ? t("modals.matchingConfigEditor.saving")
              : t("modals.matchingConfigEditor.savePreferences")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Question popover */}
      <Popover
        open={Boolean(addPopoverAnchor)}
        anchorEl={addPopoverAnchor}
        onClose={() => setAddPopoverAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box p={1} minWidth={260}>
          <Typography
            variant="caption"
            color="text.secondary"
            px={1}
            display="block"
            mb={0.5}
          >
            {t("modals.matchingConfigEditor.chooseQuestionType")}
          </Typography>
          {availableTypes.map((type) => (
            <Box
              key={type}
              onClick={() => addQuestion(type)}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {t(QUESTION_TYPE_LABEL_KEYS[type])}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(QUESTION_TYPE_SUBLABEL_KEYS[type])}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Survey preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("modals.matchingConfigEditor.previewTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("modals.matchingConfigEditor.previewBody")}
          </Typography>
          <SurveyPreview questions={draft.questions} t={t} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            {t("modals.matchingConfigEditor.previewClose")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export type { DraftConfig };
